import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["fileInput"];
  static values = {
    checkDuplicateUrl: String,
    compareAndUpdateUrl: String,
    containerId: Number,
  };

  connect() {
    console.log("DuplicateDetectionController connected");

    // Check for duplicates when files are selected
    if (this.hasFileInputTarget) {
      this.fileInputTarget.addEventListener("change", () =>
        this.onFileSelected(),
      );
    }

    // Setup modal button listeners
    this.setupModalListeners();

    // Store reference to form for later submission
    this.form = this.element.querySelector("form");
  }

  setupModalListeners() {
    const cancelBtn = document.getElementById("duplicateCancelBtn");
    const compareBtn = document.getElementById("duplicateCompareBtn");
    const uploadAnywayBtn = document.getElementById("duplicateUploadAnywayBtn");
    const updateBtn = document.getElementById("duplicateUpdateBtn");

    if (cancelBtn)
      cancelBtn.addEventListener("click", () => this.cancelUpload());
    if (compareBtn)
      compareBtn.addEventListener("click", () => this.compareContent());
    if (uploadAnywayBtn)
      uploadAnywayBtn.addEventListener("click", () => this.uploadAnyway());
    if (updateBtn)
      updateBtn.addEventListener("click", () => this.handleUpdateDocument());
  }

  async onFileSelected() {
    const files = this.fileInputTarget.files;
    if (files.length === 0) return;

    // Check first file for duplicates
    const file = files[0];
    await this.checkForDuplicates(file);
  }

  async checkForDuplicates(file) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("container_id", this.containerIdValue);

    try {
      const response = await fetch(this.checkDuplicateUrlValue, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": this.getCsrfToken(),
        },
      });

      const responseText = await response.text();

      if (!response.ok) {
        console.error("Request failed with status:", response.status);
        return;
      }

      const result = JSON.parse(responseText);

      if (result.duplicate_found) {
        this.showDuplicateModal(file, result.existing_documents[0]);
      }
    } catch (error) {
      console.error("Error checking for duplicates:", error);
    }
  }

  showDuplicateModal(file, existingDoc) {
    // Store current state
    this.currentFile = file;
    this.existingDocument = existingDoc;

    // Update modal text
    document.getElementById("duplicateFileName").textContent = file.name;

    const inCurrentContainer = existingDoc.in_current_container;
    const hasOtherContainers = existingDoc.other_container_count > 0;

    // Build container display text
    let containerText = "";
    let contextText = "";

    if (inCurrentContainer && !hasOtherContainers) {
      // Scenario 1: Document exists ONLY in current container
      containerText = existingDoc.containers.map((c) => c.name).join(", ");

      contextText =
        "<strong>⚠️ This document already exists in this container. Do you want to replace it?</strong>";
    } else {
      // Scenario 2: Document exists in other containers (with or without current)
      if (hasOtherContainers && inCurrentContainer) {
        containerText =
          "this container and " +
          existingDoc.containers
            .filter((c) => c.id !== this.containerIdValue)
            .map((c) => c.name)
            .join(", ");
      } else {
        containerText = existingDoc.containers.map((c) => c.name).join(", ");
      }

      contextText =
        "<strong>✓ This document exists in other containers and will be linked to the current one.</strong>";
    }

    document.getElementById("duplicateContainerName").textContent =
      containerText;

    // Update context message
    const contextElement = document.getElementById("duplicateContainerContext");
    contextElement.innerHTML = contextText;
    contextElement.classList.remove(
      "text-gray-600",
      "text-yellow-700",
      "bg-yellow-50",
      "p-2",
      "rounded",
    );
    contextElement.classList.add(
      "text-yellow-700",
      "bg-yellow-50",
      "p-2",
      "rounded",
    );

    // Show all 3 buttons: Cancel, Compare, Update
    document.getElementById("duplicateCancelBtn").classList.remove("hidden");
    document.getElementById("duplicateCompareBtn").classList.remove("hidden");
    document.getElementById("duplicateUpdateBtn").classList.remove("hidden");
    document.getElementById("duplicateUpdateBtn").textContent =
      "Update Document";

    // Reset comparison section
    document.getElementById("comparisonSection").classList.add("hidden");

    // Show modal
    document
      .getElementById("duplicateDetectionModal")
      .classList.remove("hidden");
  }

  async compareContent() {
    const loadingOverlay = document.getElementById("duplicateLoadingOverlay");

    loadingOverlay.classList.remove("hidden");

    const formData = new FormData();
    formData.append("file", this.currentFile);
    formData.append("container_id", this.containerIdValue);
    formData.append("existing_document_id", this.existingDocument.id);
    formData.append("action_type", "compare");

    try {
      const response = await fetch(this.compareAndUpdateUrlValue, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": this.getCsrfToken(),
        },
      });

      const comparison = await response.json();
      this.displayComparison(comparison);

      loadingOverlay.classList.add("hidden");
    } catch (error) {
      console.error("Error comparing documents:", error);
      loadingOverlay.classList.add("hidden");
    }
  }

  displayComparison(comparison) {
    const section = document.getElementById("comparisonSection");
    const results = document.getElementById("comparisonResults");

    // Clear previous results
    results.innerHTML = "";

    if (comparison.all_match) {
      results.innerHTML = `
        <div class="bg-green-50 border border-green-200 rounded p-3">
          <p class="text-green-900 font-semibold">✓ Content is identical!</p>
          <p class="text-sm text-green-800 mt-1">All ${comparison.new_document_pages} pages match exactly.</p>
        </div>
      `;
    } else {
      const diffCount = Object.keys(comparison.page_differences).length;
      const unchangedCount = comparison.new_document_pages - diffCount;

      results.innerHTML = `
        <div class="bg-yellow-50 border border-yellow-200 rounded p-3 mb-3">
          <p class="text-yellow-900 font-semibold">⚠️ Content has changed</p>
          <p class="text-sm text-yellow-800 mt-1">
            ${unchangedCount} page(s) unchanged, ${diffCount} page(s) changed
          </p>
        </div>
        <div class="space-y-2">
          ${Object.values(comparison.page_differences)
            .map(
              (diff, idx) => `
            <div class="bg-red-50 border border-red-200 rounded p-2">
              <p class="text-red-900 font-semibold">Page ${diff.page_number}: ${diff.status === "page_count_changed" ? "Page count changed" : "Content changed"}</p>
            </div>
          `,
            )
            .join("")}
        </div>
      `;
    }

    // Show update button and comparison section
    document.getElementById("duplicateUpdateBtn").classList.remove("hidden");
    document.getElementById("duplicateCompareBtn").classList.add("hidden");
    section.classList.remove("hidden");
  }

  async handleUpdateDocument() {
    const formData = new FormData();
    formData.append("file", this.currentFile);
    formData.append("container_id", this.containerIdValue);
    formData.append("existing_document_id", this.existingDocument.id);
    formData.append("action_type", "update");

    try {
      const response = await fetch(this.compareAndUpdateUrlValue, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": this.getCsrfToken(),
        },
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Redirect to container page
        if (result.redirect_to) {
          window.location.href = result.redirect_to;
        } else {
          window.location.reload();
        }
      } else {
        console.error(
          "Failed to update document:",
          result.error || response.status,
        );
        alert("Failed to update document. Please try again.");
      }
    } catch (error) {
      console.error("Error updating document:", error);
      alert("Error updating document. Please try again.");
    }
  }

  uploadAnyway() {
    // Close modal and submit form normally via Turbo
    this.hideModal();
    // Re-enable normal Turbo form submission
    if (this.form) {
      this.form.requestSubmit();
    }
  }

  cancelUpload() {
    this.hideModal();
    this.fileInputTarget.value = "";
  }

  hideModal() {
    document.getElementById("duplicateDetectionModal").classList.add("hidden");
  }

  getCsrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute("content") : "";
  }
}
